// Shared document download helper for property documents and floor plans
import { downloadAndSaveFile } from '@/core/utils/fileDownload';
import { downloadDocument } from '@/modules/profile/services/documentDownloadService';

export interface DownloadResult {
    success: boolean;
    fileUri?: string;
    fileName?: string;
    message?: string;
    error?: string;
}

const mapExtToMime = (ext: string) => {
    switch (ext) {
        case 'pdf':
            return 'application/pdf';
        case 'png':
            return 'image/png';
        case 'jpg':
        case 'jpeg':
            return 'image/jpeg';
        default:
            return 'application/octet-stream';
    }
};

export async function downloadAndPersistDocument(params: any): Promise<DownloadResult> {
    const docType = params.docType;
    const parentId = params.parentId;
    const customerId = params.customerId;
    const translationKey = params.translationKey;
    const t = params.t;

    const response = await downloadDocument(docType as any, customerId, parentId);
    if (response.status !== 'success' || !response.details?.fileContent) {
        return { success: false, error: response.message || 'Document not available' };
    }

    const ext = (response.details.extension || 'pdf').toLowerCase();
    const baseName = t(translationKey || docType) as string;
    const sanitized = baseName.replace(/[^a-zA-Z0-9]/g, '_');
    const fileName = `${sanitized}_${Date.now()}.${ext}`;

    const mimeType = mapExtToMime(ext);

    const saveResult = await downloadAndSaveFile({
        base64Content: response.details.fileContent,
        fileName,
        mimeType,
    });

    if (!saveResult.success || !saveResult.fileUri) {
        return { success: false, error: saveResult.error || 'Failed to save file' };
    }

    const baseMessage = t('propertyDetails.fileDownloadedWithName').replace('{{fileName}}', fileName);
    const hint = typeof navigator !== 'undefined' && navigator.userAgent && /iPhone|iPad|iPod/i.test(navigator.userAgent)
        ? `\n${t('propertyDetails.iosSaveHint')}`
        : '';

    return {
        success: true,
        fileUri: saveResult.fileUri,
        fileName,
        message: `${baseMessage}${hint}`,
    };
}

